import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building, 
  Shield, 
  Award, 
  Clock,
  Phone,
  CheckCircle,
  Star,
  Briefcase,
  Wrench,
  FileText,
  Factory,
  Store
} from 'lucide-react';

const CommercialRoofing: React.FC = () => {
  const handleGetQuote = () => {
    window.open('https://carolinatrophyroofs.sky-quote.com/schedule#step1', '_blank');
  };

  const handleCallNow = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const services = [
    {
      icon: Building,
      title: 'New Commercial Roofs',
      description: 'Complete commercial roofing systems for new construction and building expansions.'
    },
    {
      icon: Wrench,
      title: 'Roof Replacement',
      description: 'Full commercial roof replacement with minimal business disruption and competitive pricing.'
    },
    {
      icon: Shield,
      title: 'Preventive Maintenance',
      description: 'Scheduled maintenance programs to extend roof life and prevent costly emergency repairs.'
    },
    {
      icon: FileText,
      title: 'Roof Inspections',
      description: 'Comprehensive commercial roof inspections for due diligence, insurance, and condition assessments.'
    }
  ];

  const roofingSystems = [
    {
      name: 'TPO Roofing',
      description: 'Single-ply thermoplastic membrane offering excellent energy efficiency and durability.',
      bestFor: 'Office buildings, retail stores',
      warranty: '15-20 years'
    },
    {
      name: 'EPDM Rubber',
      description: 'Proven single-ply membrane system with excellent weather resistance and longevity.',
      bestFor: 'Warehouses, industrial facilities',
      warranty: '15-25 years'
    },
    {
      name: 'Modified Bitumen',
      description: 'Multi-layer built-up system providing superior waterproofing and durability.',
      bestFor: 'High-traffic roofs, complex designs',
      warranty: '15-20 years'
    },
    {
      name: 'Metal Roofing',
      description: 'Standing seam and panel systems offering longevity and energy efficiency.',
      bestFor: 'Manufacturing, agricultural buildings',
      warranty: '25-40 years'
    }
  ];

  const industries = [
    {
      icon: Store,
      name: 'Retail & Shopping Centers',
      description: 'Specialized roofing for retail spaces, shopping centers, and commercial plazas.'
    },
    {
      icon: Factory,
      name: 'Industrial & Manufacturing',
      description: 'Heavy-duty roofing systems for warehouses, manufacturing facilities, and industrial complexes.'
    },
    {
      icon: Building,
      name: 'Office Buildings',
      description: 'Professional roofing solutions for office complexes, corporate headquarters, and business parks.'
    },
    {
      icon: Briefcase,
      name: 'Multi-Family Housing',
      description: 'Comprehensive roofing for apartment complexes, condominiums, and housing developments.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'Commercial Expertise',
      description: 'Specialized experience in commercial roofing systems and building codes.'
    },
    {
      icon: Star,
      title: 'Proven Track Record',
      description: 'Successfully completed hundreds of commercial roofing projects across Eastern NC.'
    },
    {
      icon: Clock,
      title: 'Minimal Disruption',
      description: 'Strategic scheduling and efficient work practices to minimize business interruption.'
    },
    {
      icon: Shield,
      title: 'Comprehensive Insurance',
      description: 'Full commercial liability and workers compensation coverage for your protection.'
    }
  ];

  const serviceAreas = [
    'Rocky Mount, NC',
    'Greenville, NC', 
    'Wilson, NC',
    'Goldsboro, NC',
    'Raleigh, NC',
    'Jacksonville, NC',
    'Eastern North Carolina',
    'Surrounding Counties'
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-charcoal-gray to-gray-800 text-white">
          <div className="max-w-7xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-2 text-sm font-medium border-brand-trophy-gold text-brand-trophy-gold">
              <Building className="w-4 h-4 mr-2" />
              Commercial Roofing Specialists
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Commercial Roofing
              <span className="text-brand-trophy-gold block mt-2">Solutions for Business</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
              Professional commercial roofing services for businesses throughout Eastern North Carolina. 
              From office buildings to industrial facilities, we protect your business with reliable, long-lasting roofing systems.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={handleGetQuote}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                Get Commercial Quote
              </Button>
              <Button 
                onClick={handleCallNow}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (252) 371-2266
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center max-w-2xl mx-auto">
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">500+</div>
                <div className="text-sm text-gray-300">Commercial Projects</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">15+</div>
                <div className="text-sm text-gray-300">Years Experience</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">24/7</div>
                <div className="text-sm text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-trophy-gold">100%</div>
                <div className="text-sm text-gray-300">Licensed & Insured</div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Commercial Roofing Services
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From new construction to emergency repairs, we provide comprehensive commercial roofing solutions tailored to your business needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Roofing Systems */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Commercial Roofing Systems
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We install and maintain all major commercial roofing systems, selecting the best solution for your specific building and budget.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {roofingSystems.map((system, index) => (
                <Card key={index} className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-xl text-brand-charcoal-gray">{system.name}</CardTitle>
                    <CardDescription>{system.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-500">Best For:</span>
                        <div className="font-semibold">{system.bestFor}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Warranty:</span>
                        <div className="font-semibold">{system.warranty}</div>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full mt-4" asChild>
                      <Link to="/contact">Learn More</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Industries We Serve
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Specialized commercial roofing expertise across diverse business sectors throughout Eastern North Carolina.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <Card key={index} className="flex items-start p-6 hover:shadow-lg transition-shadow">
                  <industry.icon className="w-12 h-12 text-brand-trophy-gold mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{industry.name}</h3>
                    <p className="text-gray-600">{industry.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Businesses Choose Carolina Trophy Roofs
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Experience the difference of working with Eastern NC's most trusted commercial roofing contractor.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((feature, index) => (
                <div key={index} className="text-center">
                  <feature.icon className="w-16 h-16 text-brand-trophy-gold mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Commercial Roofing Service Areas
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              Providing professional commercial roofing services throughout Eastern North Carolina
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm border">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">
              Protect Your Business Investment
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              Get a professional assessment and competitive quote for your commercial roofing project. 
              Our experts understand the unique needs of business properties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleGetQuote}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                Get Commercial Quote
              </Button>
              <Button 
                onClick={handleCallNow}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call: (252) 371-2266
              </Button>
            </div>
          </div>
        </section>

        {/* Reference Sources */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Authoritative Commercial Roofing Resources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'National Roofing Contractors Association - Commercial', url: 'https://www.nrca.net/professional-resources' },
                { name: 'Building Owners and Managers Association', url: 'https://www.boma.org/' },
                { name: 'International Facility Management Association', url: 'https://www.ifma.org/' },
                { name: 'Commercial Real Estate Development Association', url: 'https://www.naiop.org/' },
                { name: 'U.S. General Services Administration', url: 'https://www.gsa.gov/real-estate/design-construction' },
                { name: 'American Society of Civil Engineers', url: 'https://www.asce.org/' },
                { name: 'International Code Council', url: 'https://www.iccsafe.org/' },
                { name: 'NC Department of Commerce - Business Resources', url: 'https://www.nccommerce.com/' }
              ].map((ref, index) => (
                <a 
                  key={index}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-brand-trophy-gold hover:shadow-md transition-all"
                >
                  <div className="text-sm font-medium text-brand-charcoal-gray hover:text-brand-trophy-gold">
                    {ref.name}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CommercialRoofing;